import { useEffect, useState, useCallback } from 'react';

const API_BASE_URL =
    import.meta.env.MODE === 'production'
        ? import.meta.env.VITE_BACKEND_URL || 'https://hivecore-backend-production.up.railway.app'
        : import.meta.env.VITE_BACKEND_URL || 'https://hivecore-backend-staging.up.railway.app';

function getAuthHeaders(): HeadersInit {
    const headers: HeadersInit = {
        'Content-Type': 'application/json',
    };

    // Add internal token for staging/dev if configured
    const internalToken = import.meta.env.VITE_HIVECORE_INTERNAL_TOKEN;
    if (internalToken) {
        headers['X-HiveCore-Internal-Token'] = internalToken;
    }

    // Add JWT token if available
    const jwt = import.meta.env.VITE_HIVECORE_JWT;
    if (jwt) {
        headers['Authorization'] = `Bearer ${jwt}`;
    }

    return headers;
}

interface BuilderMission {
    missionId: string;
    lastEvent: {
        type: string;
        timestamp: string;
    };
}

interface BuilderEvent {
    eventId: string;
    missionId: string;
    type: string;
    timestamp: string;
    payload: Record<string, unknown>;
}

interface CodePatch {
    id: string;
    missionId: string;
    targetFiles: string[];
    description: string;
    createdAt: string;
}

interface RepairTemplate {
    id: string;
    targetFiles: string[];
    description: string;
}

export function BuilderPanel() {
    const [missions, setMissions] = useState<BuilderMission[]>([]);
    const [patches, setPatches] = useState<CodePatch[]>([]);
    const [repairs, setRepairs] = useState<RepairTemplate[]>([]);
    const [selectedMission, setSelectedMission] = useState<string | null>(null);
    const [timeline, setTimeline] = useState<BuilderEvent[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [status, setStatus] = useState<{
        builderEnabled: boolean;
        phase7Enabled: boolean;
    } | null>(null);

    const fetchStatus = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/builder/status`, {
                headers: getAuthHeaders(),
            });
            if (!res.ok) {
                const data = await res.json();
                setError(data.error || 'Builder not available');
                return;
            }
            const data = await res.json();
            setStatus({ builderEnabled: data.builderEnabled, phase7Enabled: data.phase7Enabled });
            setError(null);
        } catch (err) {
            setError('Failed to fetch builder status');
        }
    }, []);

    const fetchMissions = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/builder/missions`, {
                headers: getAuthHeaders(),
            });
            if (!res.ok) return;
            const data = await res.json();
            setMissions(data.missions || []);
        } catch (err) {
            console.error('Failed to fetch missions:', err);
        }
    }, []);

    const fetchPatches = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/builder/patches`, {
                headers: getAuthHeaders(),
            });
            if (!res.ok) return;
            const data = await res.json();
            setPatches(data.patches || []);
        } catch (err) {
            console.error('Failed to fetch patches:', err);
        }
    }, []);

    const fetchRepairs = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/builder/repairs`, {
                headers: getAuthHeaders(),
            });
            if (!res.ok) return;
            const data = await res.json();
            setRepairs(data.templates || []);
        } catch (err) {
            console.error('Failed to fetch repairs:', err);
        }
    }, []);

    const loadTimeline = useCallback(async (missionId: string) => {
        setSelectedMission(missionId);
        try {
            const res = await fetch(`${API_BASE_URL}/api/builder/missions/${missionId}/timeline`, {
                headers: getAuthHeaders(),
            });
            if (!res.ok) return;
            const data = await res.json();
            setTimeline(data.events || []);
        } catch (err) {
            console.error('Failed to fetch timeline:', err);
        }
    }, []);

    const triggerRepair = useCallback(
        async (templateId: string) => {
            setLoading(true);
            setError(null);
            try {
                const res = await fetch(`${API_BASE_URL}/api/builder/repairs/${templateId}`, {
                    method: 'POST',
                    headers: getAuthHeaders(),
                });
                const data = await res.json();
                if (!data.success) {
                    setError(data.error || 'Repair failed');
                } else {
                    await fetchMissions();
                    await fetchPatches();
                    if (data.missionId) {
                        await loadTimeline(data.missionId);
                    }
                }
            } catch (err) {
                setError('Failed to trigger repair');
            } finally {
                setLoading(false);
            }
        },
        [fetchMissions, fetchPatches, loadTimeline]
    );

    const applyPatch = useCallback(
        async (patchId: string) => {
            setLoading(true);
            setError(null);
            try {
                const res = await fetch(`${API_BASE_URL}/api/builder/patches/${patchId}/apply`, {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify({ humanApproved: true }),
                });
                const data = await res.json();
                if (!data.success) {
                    setError(data.error || 'Apply failed');
                } else {
                    await fetchPatches();
                    const patch = patches.find(p => p.id === patchId);
                    if (patch) {
                        await loadTimeline(patch.missionId);
                    }
                }
            } catch (err) {
                setError('Failed to apply patch');
            } finally {
                setLoading(false);
            }
        },
        [fetchPatches, loadTimeline, patches]
    );

    const refreshAll = useCallback(async () => {
        setLoading(true);
        await Promise.all([fetchStatus(), fetchMissions(), fetchPatches(), fetchRepairs()]);
        setLoading(false);
    }, [fetchStatus, fetchMissions, fetchPatches, fetchRepairs]);

    useEffect(() => {
        refreshAll();
    }, [refreshAll]);

    if (error && !status) {
        return (
            <div className="panel">
                <header className="panel-header">
                    <h2>Builder Panel</h2>
                    <span className="chip warn">Disabled</span>
                </header>
                <div style={{ padding: '24px', textAlign: 'center', color: 'var(--muted)' }}>
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="panel">
            <header className="panel-header">
                <h2>Builder Panel</h2>
                <span className="chip ok">{status?.builderEnabled ? 'Enabled' : 'Disabled'}</span>
                <button onClick={refreshAll} disabled={loading} style={{ marginLeft: 'auto' }}>
                    {loading ? 'Loading...' : 'Refresh'}
                </button>
            </header>

            {error && (
                <div
                    style={{
                        padding: '8px 16px',
                        background: 'var(--error-bg)',
                        color: 'var(--error)',
                    }}
                >
                    {error}
                </div>
            )}

            <section style={{ padding: '16px' }}>
                <h3>Repair Actions</h3>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                    {repairs.map(repair => (
                        <button
                            key={repair.id}
                            onClick={() => triggerRepair(repair.id)}
                            disabled={loading}
                            title={repair.description}
                        >
                            {repair.id.replace('repair-', 'Repair ')}
                        </button>
                    ))}
                    {repairs.length === 0 && (
                        <span style={{ color: 'var(--muted)' }}>No repair templates available</span>
                    )}
                </div>
            </section>

            <section style={{ padding: '16px', borderTop: '1px solid var(--border)' }}>
                <h3>Missions ({missions.length})</h3>
                <table className="table" style={{ marginTop: '8px' }}>
                    <thead>
                        <tr>
                            <th>Mission ID</th>
                            <th>Last Event</th>
                            <th>Timestamp</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {missions.map(mission => (
                            <tr
                                key={mission.missionId}
                                style={{
                                    background:
                                        selectedMission === mission.missionId
                                            ? 'var(--selected-bg)'
                                            : undefined,
                                    cursor: 'pointer',
                                }}
                                onClick={() => loadTimeline(mission.missionId)}
                            >
                                <td>{mission.missionId}</td>
                                <td>{mission.lastEvent?.type || '-'}</td>
                                <td>
                                    {mission.lastEvent?.timestamp
                                        ? new Date(mission.lastEvent.timestamp).toLocaleString()
                                        : '-'}
                                </td>
                                <td>
                                    <button
                                        onClick={e => {
                                            e.stopPropagation();
                                            loadTimeline(mission.missionId);
                                        }}
                                    >
                                        View Timeline
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {missions.length === 0 && (
                            <tr>
                                <td
                                    colSpan={4}
                                    style={{
                                        textAlign: 'center',
                                        padding: '24px',
                                        color: 'var(--muted)',
                                    }}
                                >
                                    No missions yet. Use repair actions or create a mission.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </section>

            {selectedMission && (
                <section style={{ padding: '16px', borderTop: '1px solid var(--border)' }}>
                    <h3>Timeline: {selectedMission}</h3>
                    <table className="table" style={{ marginTop: '8px' }}>
                        <thead>
                            <tr>
                                <th>Event</th>
                                <th>Timestamp</th>
                                <th>Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            {timeline.map(event => (
                                <tr key={event.eventId}>
                                    <td>
                                        <span
                                            className={`chip ${event.type === 'ERROR' ? 'error' : 'ok'}`}
                                        >
                                            {event.type}
                                        </span>
                                    </td>
                                    <td>{new Date(event.timestamp).toLocaleString()}</td>
                                    <td>
                                        <code style={{ fontSize: '12px' }}>
                                            {JSON.stringify(event.payload, null, 2).slice(0, 100)}
                                            {JSON.stringify(event.payload).length > 100
                                                ? '...'
                                                : ''}
                                        </code>
                                    </td>
                                </tr>
                            ))}
                            {timeline.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={3}
                                        style={{
                                            textAlign: 'center',
                                            padding: '24px',
                                            color: 'var(--muted)',
                                        }}
                                    >
                                        No events in timeline.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </section>
            )}

            <section style={{ padding: '16px', borderTop: '1px solid var(--border)' }}>
                <h3>Patches ({patches.length})</h3>
                <table className="table" style={{ marginTop: '8px' }}>
                    <thead>
                        <tr>
                            <th>Patch ID</th>
                            <th>Target Files</th>
                            <th>Created</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {patches.map(patch => (
                            <tr key={patch.id}>
                                <td>{patch.id}</td>
                                <td>{patch.targetFiles?.join(', ') || '-'}</td>
                                <td>{new Date(patch.createdAt).toLocaleString()}</td>
                                <td>
                                    <button onClick={() => applyPatch(patch.id)} disabled={loading}>
                                        Apply
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {patches.length === 0 && (
                            <tr>
                                <td
                                    colSpan={4}
                                    style={{
                                        textAlign: 'center',
                                        padding: '24px',
                                        color: 'var(--muted)',
                                    }}
                                >
                                    No patches generated yet.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </section>
        </div>
    );
}
